import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Crosshair, Plus, Trash2, Download, MapPin, X, Pencil, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

// Fix Leaflet default icons in bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = [45.7833, 14.2167]; // Rakek – enako kot HuntingMapPage
const VRSTE_DIVJADI = [
  "srnjad",
  "jelenjad",
  "divji prašič",
  "damjak",
  "muflon",
  "gams",
  "medved",
  "šakal",
  "lisica",
  "zajec",
  "poljski jerebica",
  "fazan",
  "raca",
  "drugo",
];
const SMERI = ["S", "SV", "V", "JV", "J", "JZ", "Z", "SZ"];

const LEADERSHIP = ["predsednik", "gospodar", "administrator", "super_admin", "tajnik"];

const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("sl-SI", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

function MapClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

const emptyForm = {
  datum: new Date().toISOString().slice(0, 10),
  ura: "",
  vrsta_divjadi: "",
  krajevno_ime: "",
  smer_strela: "",
  lat: null,
  lng: null,
  opomba: "",
};

const MissedShotsPage = () => {
  const { token, user } = useAuth();
  const isLeader = LEADERSHIP.includes(user?.vloga);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [editId, setEditId] = useState(null); // null = new; string id = editing

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/missed-shots`, {
        headers,
        params: year ? { year } : {},
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Napaka pri branju zgrešenih strelov");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year]);

  const openNew = () => {
    setEditId(null);
    setForm(emptyForm);
    setMapCenter(DEFAULT_CENTER);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      datum: row.datum || "",
      ura: row.ura || "",
      vrsta_divjadi: row.vrsta_divjadi || "",
      krajevno_ime: row.krajevno_ime || "",
      smer_strela: row.smer_strela || "",
      lat: row.lat ?? null,
      lng: row.lng ?? null,
      opomba: row.opomba || "",
    });
    if (row.lat != null && row.lng != null) setMapCenter([row.lat, row.lng]);
    else setMapCenter(DEFAULT_CENTER);
    setDialogOpen(true);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Naprava ne podpira lokacije");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setForm((f) => ({ ...f, lat, lng }));
        setMapCenter([lat, lng]);
        toast.success("Lokacija zajeta");
      },
      () => toast.error("Ni bilo mogoče pridobiti lokacije"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSave = async () => {
    if (!form.datum) {
      toast.error("Datum je obvezen");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/missed-shots/${editId}`, form, { headers });
        toast.success("Zapis posodobljen");
      } else {
        await axios.post(`${API}/missed-shots`, form, { headers });
        toast.success("Zgrešeni strel prijavljen");
      }
      setDialogOpen(false);
      setEditId(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri shranjevanju");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ali res želite izbrisati ta zapis?")) return;
    try {
      await axios.delete(`${API}/missed-shots/${id}`, { headers });
      toast.success("Zapis izbrisan");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri brisanju");
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/missed-shots/export/${year}`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `zgreseni_streli_${year}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Napaka pri izvozu");
    }
  };

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2, y - 3, y - 4];
  }, []);

  const canDelete = (row) =>
    isLeader || row.clan_id === user?.id;

  // ===== Statistika (vodstvo) =====
  const statsByMember = useMemo(() => {
    if (!isLeader) return [];
    const map = new Map();
    for (const r of rows) {
      const name = r.clan_ime || "—";
      map.set(name, (map.get(name) || 0) + 1);
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 12);
  }, [rows, isLeader]);

  const statsByAnimal = useMemo(() => {
    if (!isLeader) return [];
    const map = new Map();
    for (const r of rows) {
      const v = r.vrsta_divjadi || "neznano";
      map.set(v, (map.get(v) || 0) + 1);
    }
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [rows, isLeader]);

  const PIE_COLORS = ["#c58a3f", "#7f8f4a", "#5e6b7a", "#a67b5b", "#4b6b5b", "#8f6b3f", "#6b5e4b", "#9a7f5e"];

  return (
    <div className="space-y-6" data-testid="missed-shots-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Crosshair className="w-7 h-7 text-primary" />
            Prijava zgrešenega strela
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isLeader
              ? "Vodstvo vidi vse zapise v LD."
              : "Vidiš samo svoje zapise."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="w-[110px]" data-testid="ms-year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExport} data-testid="ms-export-btn">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button onClick={openNew} data-testid="ms-new-btn">
            <Plus className="w-4 h-4 mr-2" /> Prijavi zgrešen strel
          </Button>
        </div>
      </div>

      {isLeader && rows.length > 0 && (
        <Card data-testid="ms-stats-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Statistika sezone {year}
            </CardTitle>
            <CardDescription>
              Skupaj {rows.length} zgrešenih strelov v LD. Vidno samo vodstvu.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium mb-2">Po članih (top 12)</p>
                <div className="h-[280px]" data-testid="ms-chart-members">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={statsByMember} layout="vertical" margin={{ left: 20, right: 12, top: 5, bottom: 5 }}>
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#c58a3f" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Po vrstah divjadi</p>
                <div className="h-[280px]" data-testid="ms-chart-animals">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statsByAnimal}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={(e) => `${e.name} (${e.value})`}
                      >
                        {statsByAnimal.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend />
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Seznam ({rows.length})</CardTitle>
          <CardDescription>
            Sortirano od najnovejšega. CSV izvoz vključuje vse podatke tekočega leta.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground text-sm">Nalagam…</p>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="ms-empty-state">
              Ni zapisov za leto {year}.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="ms-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Ura</TableHead>
                    {isLeader && <TableHead>Član</TableHead>}
                    <TableHead>Vrsta divjadi</TableHead>
                    <TableHead>Krajevno ime</TableHead>
                    <TableHead>Smer</TableHead>
                    <TableHead>GPS</TableHead>
                    <TableHead>Opomba</TableHead>
                    {isLeader && <TableHead>Vpisano</TableHead>}
                    {isLeader && <TableHead>Zadnja sprememba</TableHead>}
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} data-testid={`ms-row-${r.id}`}>
                      <TableCell className="whitespace-nowrap">{r.datum}</TableCell>
                      <TableCell>{r.ura || "—"}</TableCell>
                      {isLeader && <TableCell>{r.clan_ime}</TableCell>}
                      <TableCell>{r.vrsta_divjadi || "—"}</TableCell>
                      <TableCell>{r.krajevno_ime || "—"}</TableCell>
                      <TableCell>{r.smer_strela ? <Badge variant="outline">{r.smer_strela}</Badge> : "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {r.lat != null && r.lng != null ? (
                          <a
                            href={`https://www.google.com/maps?q=${r.lat},${r.lng}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 hover:underline"
                          >
                            <MapPin className="w-3 h-3" />
                            {r.lat.toFixed(4)}, {r.lng.toFixed(4)}
                          </a>
                        ) : "—"}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate">{r.opomba || "—"}</TableCell>
                      {isLeader && (
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {fmtTs(r.ustvarjeno)}
                        </TableCell>
                      )}
                      {isLeader && (
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {r.posodobljeno ? (
                            <span title={r.posodobil_ime || ""}>
                              {fmtTs(r.posodobljeno)}
                              {r.posodobil_ime ? ` · ${r.posodobil_ime}` : ""}
                            </span>
                          ) : "—"}
                        </TableCell>
                      )}
                      <TableCell>
                        {canDelete(r) && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(r)}
                              data-testid={`ms-edit-${r.id}`}
                              title="Uredi"
                            >
                              <Pencil className="w-4 h-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(r.id)}
                              data-testid={`ms-delete-${r.id}`}
                              title="Izbriši"
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Uredi zgrešen strel" : "Prijava zgrešenega strela"}</DialogTitle>
            <DialogDescription>
              Zabeleži datum, uro, vrsto divjadi in po želji lokacijo na zemljevidu.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Datum *</Label>
              <Input
                type="date"
                value={form.datum}
                onChange={(e) => setForm({ ...form, datum: e.target.value })}
                data-testid="ms-form-datum"
              />
            </div>
            <div>
              <Label>Ura</Label>
              <Input
                type="time"
                value={form.ura || ""}
                onChange={(e) => setForm({ ...form, ura: e.target.value })}
                data-testid="ms-form-ura"
              />
            </div>
            <div>
              <Label>Vrsta divjadi</Label>
              <Select
                value={form.vrsta_divjadi || ""}
                onValueChange={(v) => setForm({ ...form, vrsta_divjadi: v })}
              >
                <SelectTrigger data-testid="ms-form-vrsta">
                  <SelectValue placeholder="Izberi…" />
                </SelectTrigger>
                <SelectContent>
                  {VRSTE_DIVJADI.map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Krajevno ime</Label>
              <Input
                value={form.krajevno_ime || ""}
                onChange={(e) => setForm({ ...form, krajevno_ime: e.target.value })}
                placeholder="npr. Krvavica"
                data-testid="ms-form-kraj"
              />
            </div>
            <div>
              <Label>Smer strela</Label>
              <Select
                value={form.smer_strela || ""}
                onValueChange={(v) => setForm({ ...form, smer_strela: v })}
              >
                <SelectTrigger data-testid="ms-form-smer">
                  <SelectValue placeholder="Izberi smer…" />
                </SelectTrigger>
                <SelectContent>
                  {SMERI.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>GPS lokacija</Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={
                    form.lat != null && form.lng != null
                      ? `${form.lat.toFixed(5)}, ${form.lng.toFixed(5)}`
                      : "Klik na zemljevid ali gumb"
                  }
                  data-testid="ms-form-latlng"
                />
                <Button type="button" variant="outline" size="sm" onClick={useMyLocation}>
                  <MapPin className="w-4 h-4 mr-1" /> Moja
                </Button>
                {form.lat != null && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setForm({ ...form, lat: null, lng: null })}
                    title="Odstrani lokacijo"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Opomba</Label>
              <Textarea
                rows={2}
                value={form.opomba || ""}
                onChange={(e) => setForm({ ...form, opomba: e.target.value })}
                placeholder="Kratek opis situacije, oddaljenost, razlog…"
                data-testid="ms-form-opomba"
              />
            </div>
          </div>

          <div className="mt-2">
            <Label className="mb-1 block">Zemljevid – klikni za pin (neobvezno)</Label>
            <div className="h-[320px] rounded-lg overflow-hidden border">
              <MapContainer
                center={mapCenter}
                zoom={12}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapClickHandler
                  onPick={(p) => setForm((f) => ({ ...f, lat: p.lat, lng: p.lng }))}
                />
                {form.lat != null && form.lng != null && (
                  <Marker position={[form.lat, form.lng]} />
                )}
              </MapContainer>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Prekliči
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="ms-save-btn">
              {saving ? "Shranjujem…" : "Shrani"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MissedShotsPage;
